import { Progress, ProgressProps } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import { getScoreGrade } from "../../../../common/Grade";
import { roundToTwoDecimals } from "../../../../common/Number";
import { ScoreMarker } from "./ScoreMarker";
import { VersionMarker } from "./VersionMarker";
import classes from "./DealScoreBar.module.less";

export interface VersionScore {
    version: number;
    label: string;
    score: number;
}

export interface DealScoreBarProps extends ProgressProps {
    score?: number;
    perfectScore?: number;
    showTitle?: boolean;
    showPerfect?: boolean;
    strokeWidth?: number;
    className?: string;
    scoreSize?: number;
    versionScores?: VersionScore[];
}

export const DealScoreBar: FC<DealScoreBarProps> = ({
    score = 0,
    perfectScore = 90,
    showTitle = true,
    showPerfect = true,
    strokeWidth = 54,
    className,
    scoreSize,
    versionScores,
}) => {
    const _score = roundToTwoDecimals(score);
    const _perfectScore = roundToTwoDecimals(perfectScore);
    const grade = getScoreGrade(_score).grade;

    const scoreRef = useRef<HTMLSpanElement>(null);
    const [scoreWidth, setScoreWidth] = useState(0);

    const minLeft = 10 + scoreWidth;

    useEffect(() => {
        if (scoreRef.current) {
            setScoreWidth(scoreRef.current.offsetWidth);
        }
    }, [_score]);

    return (
        <div className={clsx(classes.container, className)}>
            <div className={classes.content}>
                {showTitle && <div className={classes.title}>Overall contract value</div>}
                <div className={classes.scoreBar}>
                    <div className={classes.progressWrapper}>
                        <Progress
                            strokeColor={{
                                "25%": "#FD3559",
                                "50%": "#FF981F",
                                "75%": "#77E043",
                                "100%": "#33C701",
                            }}
                            showInfo={false}
                            strokeWidth={strokeWidth}
                            percent={100}
                            className={classes.progress}
                        ></Progress>
                        <div className={classes.trail} style={{ width: `${100 - score}%` }}></div>
                    </div>
                    {versionScores
                        ?.filter(
                            (vs) => vs.version < Math.max(...versionScores.map((v) => v.version))
                        )
                        .map((vs, index) => (
                            <VersionMarker
                                key={vs.version}
                                className={classes.marker}
                                version={vs.version}
                                score={roundToTwoDecimals(vs.score)}
                                labelPosition={index % 2 === 0 ? "above" : "below"}
                                style={{ left: `${vs.score}%` }}
                            />
                        ))}
                    {showPerfect && _score < _perfectScore ? (
                        <ScoreMarker
                            className={classes.marker}
                            score={_perfectScore}
                            style={{ left: `${_perfectScore}%` }}
                        />
                    ) : null}
                    <span
                        ref={scoreRef}
                        className={classes.score}
                        style={{
                            left: `max(${minLeft}px,calc(${_score}% - 10px))`,
                            fontSize: scoreSize,
                        }}
                    >
                        {_score}% ({grade})
                    </span>
                </div>
            </div>
        </div>
    );
};
